import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export const useOrderNotifications = () => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastMessage, setLastMessage] = useState(null);

  // WebSocket connection URL
  const getWebSocketUrl = useCallback(() => {
    const token = localStorage.getItem('authToken');
    const defaultApi = `${window.location.origin}/api`;
    const apiBase = process.env.REACT_APP_API_URL || defaultApi;

    // Derive WebSocket protocol from current page protocol
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // Extract host from apiBase and strip any trailing /api
    let host;
    try {
      const u = new URL(apiBase);
      host = u.host;
    } catch (_) {
      host = apiBase.replace(/^https?:\/\//, '');
    }
    host = host.replace(/\/?api\/?$/, '');

    return `${protocol}//${host}/ws?token=${token}`;
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!user || user.role !== 'admin') {
      console.log('User is not admin, skipping WebSocket connection');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    try {
      setConnectionStatus('connecting');
      const wsUrl = getWebSocketUrl();
      console.log('🔌 Connecting to WebSocket:', wsUrl);

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        setIsConnected(true);
        setConnectionStatus('connected');
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket message received:', data);
          console.log('📨 Message type:', data.type);
          setLastMessage(data);

          // Handle different message types
          switch (data.type) {
            case 'connection_established':
              console.log('✅ WebSocket connection established');
              break;
            case 'pong':
              console.log('🏓 Pong received');
              break;
            case 'new_order':
              console.log('📦 New order notification received:', data.order);
              console.log('📦 Order details:', {
                id: data.order.id,
                order_number: data.order.order_number,
                customer_name: data.order.customer_name,
                total_amount: data.order.total_amount
              });
              // This will be handled by the notification system
              break;
            case 'system_message':
              console.log('📢 System message received:', data.message);
              break;
            default:
              console.log('📨 Unknown message type:', data.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('🔌 WebSocket connection closed:', event.code, event.reason);
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && event.code !== 1001) {
          console.log('🔄 Attempting to reconnect in 5 seconds...');
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setConnectionStatus('error');
      };

    } catch (error) {
      console.error('❌ Error creating WebSocket connection:', error);
      setConnectionStatus('error');
    }
  }, [user, getWebSocketUrl]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'User initiated disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);

  // Reconnect manually
  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(connect, 1000);
  }, [disconnect, connect]);

  // Connect on mount and when user changes
  useEffect(() => {
    if (user && user.role === 'admin') {
      connect();
    } else {
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [user, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    connectionStatus,
    lastMessage,
    connect,
    disconnect,
    reconnect
  };
};
